import type { Marker } from '@googlemaps/markerclusterer'
import {
  AdvancedMarker,
  Map as GoogleMap,
  useMap
} from '@vis.gl/react-google-maps'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_CENTER, DEFAULT_ZOOM, MIN_ZOOM } from 'src/constants'
import useBreakpoints from '../../hooks/useBreakpoints'
import { ICoordinates, ILocation } from '../../types/graphql'
import { IBounds } from '../../types/types'
import LocationMapButton from '../LocationCard/LocationMapButton'
import LocationMapCard, {
  FloatingCardContainer
} from '../LocationCard/LocationMapCard'
import { MAP_IDS } from './mapIds'
import { spreadMarkers } from './utils'

const Clusterer = dynamic(() => import('./Clusterer'), { ssr: false })

type IMapProps = {
  center?: ICoordinates
  zoom?: number
  maxZoom?: number
  onCenterChange?: (coords: ICoordinates) => void
  onBoundsChange?: (bounds: IBounds) => void
  onZoomChange?: (zoom: number) => void
  onIdleChange?: (coords: ICoordinates, zoom: number) => void
  markers?: ILocation[]
  markersButtons?: ILocation[]
  /**
   * Subordinate pins, drawn BELOW `markersButtons` and never clustered with
   * them. Built for directory rows: unclaimed facilities that belong on the
   * map (a map with two pins in a real neighbourhood is the bug) but must
   * never read as equal to monetized supply.
   *
   * The hierarchy is carried by form, not colour: a real listing gets a price
   * bubble, these get a quiet dot. That distinction is free — only ~21% of
   * directory rows have a price at all, so there is nothing to put in a
   * bubble.
   */
  subordinateMarkers?: ILocation[]
  onSubordinateMarkerClick?: (marker: ILocation) => void
  /**
   * Id of the subordinate marker to highlight — the map half of the
   * hover link with the list. Deliberately NOT `selectedMarker`: that opens
   * the floating `LocationMapCard`, which reads price, photos and rating off
   * a real listing and has none of them for a directory row.
   */
  highlightedSubordinateId?: string | null
  selectedMarker?: ILocation
  onMarkerClick?: (marker?: ILocation) => void
  animation?: boolean
  white?: boolean
  floatingCard?: boolean
  floatingCardPBM?: string
  mapTypeControl?: boolean
  greedy?: boolean
  disableZoom?: boolean
  clustering?: boolean
  /** When true, the single marker can be dragged to update coordinates. Requires onCenterChange and a single marker. */
  draggableMarker?: boolean
  /** When true, shows a button to recenter the map on the marker. */
  recenterControl?: boolean
  /** When true, always show zoom controls (including on mobile). */
  showZoomControl?: boolean
}

function MapComponent({
  center,
  zoom,
  maxZoom,
  onCenterChange,
  onBoundsChange,
  onZoomChange,
  onIdleChange,
  markers = [],
  markersButtons = [],
  subordinateMarkers = [],
  onSubordinateMarkerClick,
  highlightedSubordinateId,
  selectedMarker,
  onMarkerClick = () => {},
  animation,
  white,
  floatingCard,
  floatingCardPBM,
  mapTypeControl,
  greedy,
  disableZoom,
  clustering,
  draggableMarker,
  recenterControl,
  showZoomControl
}: IMapProps) {
  const { isUpMd } = useBreakpoints()
  const isEditableCoords =
    draggableMarker &&
    markers.length === 1 &&
    markers[0]?.coordinates &&
    onCenterChange
  const effectiveZoomControl = showZoomControl ?? (!disableZoom && isUpMd)

  const [markersState, setMarkersState] = useState<{ [key: string]: Marker }>(
    {}
  )
  const map = useMap()

  // Clustering is the only consumer of markersState. When clustering is off
  // (e.g. /search page), we must not touch state from the ref callback — a
  // new inline ref every parent render causes React to detach+attach, which
  // would toggle markersState on and off forever (error #185).
  const setMarkerRef = useCallback(
    (marker: Marker | null, key: string) => {
      if (!clustering) return
      setMarkersState((prev) => {
        if (marker) {
          if (prev[key] === marker) return prev
          return { ...prev, [key]: marker }
        }
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    [clustering]
  )

  // Cache a stable ref callback per marker id so React doesn't see a new
  // ref identity on every parent render. Without this, every render of
  // SearchManager (e.g. from onIdleChange → URL push → filters recompute)
  // would detach and re-attach every AdvancedMarker ref.
  const refCallbacksRef = useRef<Record<string, (m: Marker | null) => void>>({})
  const getMarkerRef = useCallback(
    (key: string) => {
      if (!refCallbacksRef.current[key]) {
        refCallbacksRef.current[key] = (marker: Marker | null) =>
          setMarkerRef(marker, key)
      }
      return refCallbacksRef.current[key]
    },
    [setMarkerRef]
  )

  const handleCameraChange = useCallback(
    (ev: {
      detail: { center: { lat: number; lng: number }; zoom: number }
    }) => {
      const newCenter = {
        lat: ev.detail.center.lat,
        lng: ev.detail.center.lng
      }
      const newZoom = ev.detail.zoom

      if (onCenterChange && !isEqual(center, newCenter)) {
        onCenterChange(newCenter)
      }

      if (onZoomChange && zoom !== newZoom) {
        onZoomChange(newZoom)
      }

      if (onBoundsChange) {
        const bounds = map?.getBounds()
        if (bounds) {
          const newBounds = {
            latTop: bounds.getNorthEast().lat(),
            latBot: bounds.getSouthWest().lat(),
            lngLeft: bounds.getSouthWest().lng(),
            lngRight: bounds.getNorthEast().lng()
          }
          onBoundsChange(newBounds)
        }
      }

      if (onIdleChange) {
        onIdleChange(newCenter, newZoom)
      }
    },
    [
      map,
      onCenterChange,
      onZoomChange,
      onBoundsChange,
      onIdleChange,
      center,
      zoom
    ]
  )

  useEffect(() => {
    if (center && map) {
      map.setCenter(new google.maps.LatLng(center.lat, center.lng))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng, map])

  useEffect(() => {
    if (zoom && map) {
      map.setZoom(zoom)
    }
  }, [zoom, map])

  const spreadMarkersList = useMemo(
    () => spreadMarkers(markers, zoom ?? DEFAULT_ZOOM),
    [markers, zoom]
  )
  const spreadMarkersButtonsList = useMemo(
    () => spreadMarkers(markersButtons, zoom ?? DEFAULT_ZOOM),
    [markersButtons, zoom]
  )

  const editableMarker = isEditableCoords ? spreadMarkersList[0] : null
  const displayMarkersList = isEditableCoords
    ? spreadMarkersList.slice(1)
    : spreadMarkersList

  const handleMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng
      if (latLng && onCenterChange) {
        const coords: ICoordinates = {
          lat: latLng.lat(),
          lng: latLng.lng()
        }
        onCenterChange(coords)
        if (map) {
          map.panTo(latLng)
        }
      }
    },
    [onCenterChange, map]
  )

  const handleRecenter = useCallback(() => {
    if (center && map) {
      map.panTo(new google.maps.LatLng(center.lat, center.lng))
      if (zoom) {
        map.setZoom(zoom)
      }
    }
  }, [center, zoom, map])

  const { t } = useTranslation('common')

  return (
    <div className="relative size-full">
      {clustering && (
        <Clusterer
          map={map}
          clustering={clustering}
          markersState={markersState}
        />
      )}

      <GoogleMap
        defaultCenter={center ?? DEFAULT_CENTER}
        defaultZoom={zoom ?? DEFAULT_ZOOM}
        minZoom={MIN_ZOOM}
        maxZoom={maxZoom}
        mapId={white ? MAP_IDS.white : MAP_IDS.retail}
        reuseMaps
        mapTypeControl={mapTypeControl ?? false}
        fullscreenControl={false}
        streetViewControl={false}
        scaleControl={false}
        rotateControl={false}
        cameraControl={false}
        zoomControl={effectiveZoomControl}
        gestureHandling={greedy ? 'greedy' : undefined}
        onCameraChanged={handleCameraChange}
        onClick={() => selectedMarker && isUpMd && onMarkerClick()}
        clickableIcons={false}
      >
        {selectedMarker && !floatingCard && !editableMarker && (
          <AdvancedMarker
            position={selectedMarker.coordinates ?? center}
            onClick={() => onMarkerClick()}
            clickable={true}
          >
            <LocationMapCard
              location={selectedMarker}
              onMarkerClick={onMarkerClick}
            />
          </AdvancedMarker>
        )}

        {editableMarker && editableMarker.coordinates && (
          <AdvancedMarker
            key={editableMarker.id}
            position={editableMarker.coordinates}
            draggable={true}
            onDragEnd={handleMarkerDragEnd}
            title={t('MAP.DRAG_MARKER_HINT')}
          >
            <Image
              src="/images/map_icon.png"
              alt="map icon"
              width={48}
              height={48}
              className="cursor-grab rounded-full active:cursor-grabbing"
            />
          </AdvancedMarker>
        )}

        {/* Drawn before the real markers, with an explicit low zIndex, so a
            directory pin can never sit on top of monetized supply. No
            `getMarkerRef` on purpose: `markersState` feeds the clusterer, and
            these must not be clustered together with real listings. */}
        {subordinateMarkers.map((marker) => {
          const { id, coordinates } = marker
          if (!coordinates) return null
          const isHighlighted = highlightedSubordinateId === id
          return (
            <AdvancedMarker
              key={`subordinate-${id}`}
              position={coordinates}
              onClick={() => onSubordinateMarkerClick?.(marker)}
              clickable={!!onSubordinateMarkerClick}
              // Bumped only among its OWN tier when highlighted — still far
              // below a real listing's marker either way.
              zIndex={isHighlighted ? 2 : 1}
              // Centre the (mostly transparent) hit area on the coordinate.
              // Without this the default bottom-centre anchor would hang the
              // whole 40px box above the point and the visible dot would sit
              // ~20px north of the facility.
              anchorPoint={['50%', '50%']}
              title={marker.title ?? undefined}
            >
              {/* Transparent tap target around a small dot. The dot alone was
                  10px — far under the 24px WCAG 2.5.8 minimum and unusable
                  with a finger. 40px on touch, 24px from `md:` where a cursor
                  makes the extra area unnecessary and overlapping boxes would
                  start stealing each other's clicks. */}
              <span className="flex size-10 cursor-pointer items-center justify-center md:size-6">
                <span
                  aria-hidden="true"
                  className={`block size-3 rounded-full shadow-[0_1px_3px_rgba(18,38,63,0.4)] ring-2 transition-transform duration-150 ${
                    isHighlighted
                      ? 'scale-[1.6] bg-ink-700 ring-white-500'
                      : 'bg-ink-300 ring-white-500/90'
                  }`}
                />
              </span>
            </AdvancedMarker>
          )
        })}

        {spreadMarkersButtonsList.map((marker) => {
          const { id, coordinates } = marker
          const isSelected = selectedMarker?.id === id

          return (
            <AdvancedMarker
              key={id}
              position={coordinates}
              onClick={() => onMarkerClick(marker)}
              ref={getMarkerRef(id)}
              clickable={true}
              zIndex={isSelected ? 10000 : undefined}
            >
              <LocationMapButton
                location={marker}
                onClick={onMarkerClick}
                isSelected={isSelected}
              />
            </AdvancedMarker>
          )
        })}

        {displayMarkersList.map((marker) => {
          const { id, coordinates } = marker
          const isSelected = selectedMarker?.id === id
          const url = isSelected
            ? '/images/map_icon.png'
            : '/images/map_icon_blue.png'

          if (!coordinates) return null
          return (
            <AdvancedMarker
              key={id}
              position={coordinates}
              onClick={() => onMarkerClick(marker)}
              ref={getMarkerRef(id)}
              clickable={true}
            >
              <Image
                src={url}
                alt="map icon"
                width={40}
                height={40}
                className={`rounded-full ${animation ? 'animate-bounce' : ''}`}
              />
            </AdvancedMarker>
          )
        })}
      </GoogleMap>

      {recenterControl && center && (
        <button
          type="button"
          onClick={handleRecenter}
          className="absolute bottom-4 left-4 z-10 rounded-xl border border-grey-400 bg-white-500 px-4 py-2 text-base font-semibold text-black-500 shadow-md transition hover:bg-grey-100 active:scale-95"
          title={t('MAP.RECENTER')}
          aria-label={t('MAP.RECENTER')}
        >
          {t('MAP.RECENTER')}
        </button>
      )}

      {selectedMarker && floatingCard && (
        <FloatingCardContainer pbm={floatingCardPBM}>
          <div className="pointer-events-auto">
            <LocationMapCard
              location={selectedMarker}
              onMarkerClick={onMarkerClick}
            />
          </div>
        </FloatingCardContainer>
      )}
    </div>
  )
}

export default React.memo(MapComponent)
